import React, { useState, useRef } from 'react';
import { TbMarkdown, TbFileText, TbChevronDown } from 'react-icons/tb';
import { useMasterPasswordStore } from '@/store/masterPasswordStore';

interface CreateNoteButtonProps {
  onCreate: (type: 'MDX' | 'TXT', encrypted?: boolean) => void;
  defaultEncrypted?: boolean;
}

const CreateNoteButton: React.FC<CreateNoteButtonProps> = ({ onCreate, defaultEncrypted = false }) => {
  const hasMasterKey = useMasterPasswordStore((state) => Boolean(state.masterKey));
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openDropdown = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setDropdownOpen(true);
  };

  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setDropdownOpen(false), 120);
  };

  const handleSelect = (type: 'MDX' | 'TXT', encrypted?: boolean) => {
    setDropdownOpen(false);
    onCreate(type, encrypted);
  };

  return (
    <div
      className="relative flex"
      onMouseLeave={scheduleClose}
    >
      {/* Primary action — always creates MDX */}
      <button
        className="flex-1 sm:flex-none bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-l shadow transition"
        onClick={() => handleSelect('MDX')}
      >
        + Create Note
      </button>

      {/* Chevron — opens type picker */}
      <button
        className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-2 rounded-r shadow border-l border-blue-500 transition"
        onMouseEnter={openDropdown}
        onClick={() => setDropdownOpen((o) => !o)}
        aria-label="Choose note type"
        aria-expanded={dropdownOpen}
      >
        <TbChevronDown className={`text-base transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {dropdownOpen && (
        <div
          className="absolute right-0 top-full mt-1 z-50 min-w-[170px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl"
          onMouseEnter={openDropdown}
          onMouseLeave={scheduleClose}
        >
          <button
            type="button"
            onClick={() => handleSelect('MDX')}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg transition"
          >
            <TbMarkdown className="text-lg shrink-0" />
            Markdown
          </button>
          {hasMasterKey && (
            <>
              <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
              <button type="button" onClick={() => handleSelect('MDX', !defaultEncrypted)} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700">
                {defaultEncrypted ? 'Unencrypted Markdown' : 'Encrypted Markdown'}
              </button>
              <button type="button" onClick={() => handleSelect('TXT', !defaultEncrypted)} className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg">
                {defaultEncrypted ? 'Unencrypted Plain Text' : 'Encrypted Plain Text'}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => handleSelect('TXT')}
            className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg transition"
          >
            <TbFileText className="text-lg shrink-0" />
            Plain Text
          </button>
        </div>
      )}
    </div>
  );
};

export default CreateNoteButton;
